import { useEffect, useMemo, useState } from "react";
import { Header } from "./components/Header";
import { ProgressStepper, STAGES, type StageKey } from "./components/ProgressStepper";
import { SCENARIOS } from "./data/scenarios";
import type {
  ActionDecision,
  CannedScenario,
  ExecutionResult,
} from "./data/types";
import { Queue } from "./screens/Queue";
import { InvestigateLanding } from "./screens/InvestigateLanding";
import { TriageInFlight } from "./screens/TriageInFlight";
import { TriageCard } from "./screens/TriageCard";
import { ActionPlan } from "./screens/ActionPlan";
import { Execution } from "./screens/Execution";
import { Resolution } from "./screens/Resolution";
import { Workflow } from "./screens/Workflow";

type Tab = "queue" | "workflow";

const RESOLVED_KEY = "agentic-sirt-demo.resolved-incidents.v1";

function loadResolved(): Set<string> {
  try {
    const raw = localStorage.getItem(RESOLVED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function saveResolved(set: Set<string>) {
  try {
    localStorage.setItem(RESOLVED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
}

// Stage rank helper: which stage is "furthest"
const STAGE_RANK = new Map<StageKey, number>(STAGES.map((s, i) => [s.key, i]));
function maxStage(a: StageKey, b: StageKey): StageKey {
  return (STAGE_RANK.get(a) ?? 0) >= (STAGE_RANK.get(b) ?? 0) ? a : b;
}

export function App() {
  const [tab, setTab] = useState<Tab>("queue");
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null);
  const [stage, setStage] = useState<StageKey>("investigate");
  const [reachedMax, setReachedMax] = useState<StageKey>("investigate");
  const [decisions, setDecisions] = useState<Map<string, ActionDecision>>(
    new Map(),
  );
  const [executionResults, setExecutionResults] = useState<
    Map<string, ExecutionResult>
  >(new Map());
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() => loadResolved());

  useEffect(() => {
    saveResolved(resolvedIds);
  }, [resolvedIds]);

  const scenario: CannedScenario | null = useMemo(
    () => (scenarioIdx === null ? null : SCENARIOS[scenarioIdx] ?? null),
    [scenarioIdx],
  );

  const inIncident = tab === "queue" && scenario !== null;

  const advance = (next: StageKey) => {
    setStage(next);
    setReachedMax((prev) => maxStage(prev, next));
  };

  const backToQueue = () => {
    setScenarioIdx(null);
    setStage("investigate");
    setReachedMax("investigate");
    setDecisions(new Map());
    setExecutionResults(new Map());
  };

  const closeIncident = () => {
    if (scenario) {
      const next = new Set(resolvedIds);
      next.add(scenario.incident.incident_id);
      setResolvedIds(next);
    }
    backToQueue();
  };

  const onTabChange = (t: Tab) => {
    setTab(t);
    if (t === "queue") {
      // Going back to the queue mid-incident closes the scratchpad
      if (scenarioIdx !== null) backToQueue();
    }
  };

  return (
    <div className="shell">
      <Header activeTab={tab} onTabChange={onTabChange} />

      {inIncident && scenario && (
        <div className="stepper-bar">
          <div className="stepper-inner">
            <ProgressStepper
              current={stage}
              reachedMax={reachedMax}
              onNavigate={(k) => setStage(k)}
            />
            <div className="stepper-ctx">
              <span className="badge severity-{scenario.triage_card.severity.toLowerCase()}-mini" />
              <span className="mono stepper-id">{scenario.incident.incident_id}</span>
              <button type="button" className="stepper-close" onClick={backToQueue}>
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="content">
        {tab === "workflow" && <Workflow />}

        {tab === "queue" && !scenario && (
          <Queue
            scenarios={SCENARIOS}
            resolvedIds={resolvedIds}
            onSelect={(idx) => {
              if (resolvedIds.has(SCENARIOS[idx].incident.incident_id)) return;
              setScenarioIdx(idx);
              setDecisions(new Map());
              setExecutionResults(new Map());
              setStage("investigate");
              setReachedMax("investigate");
            }}
            onClearResolved={() => setResolvedIds(new Set())}
          />
        )}

        {tab === "queue" && scenario && stage === "investigate" && (
          <InvestigateLanding
            scenario={scenario}
            onInvestigate={() => advance("triage")}
          />
        )}

        {tab === "queue" && scenario && stage === "triage" && (
          <TriageInFlight
            scenario={scenario}
            onComplete={() => advance("card")}
          />
        )}

        {tab === "queue" && scenario && stage === "card" && (
          <TriageCard
            scenario={scenario}
            onContinue={() => advance("plan")}
            onEscalate={() => alert("Triage card escalated to L2. (Demo: no live escalation routing.)")}
          />
        )}

        {tab === "queue" && scenario && stage === "plan" && (
          <ActionPlan
            scenario={scenario}
            decisions={decisions}
            setDecisions={setDecisions}
            onSubmit={() => {
              const initial = new Map<string, ExecutionResult>();
              for (const action of scenario.action_plan.actions) {
                const dec = decisions.get(action.action_id);
                if (dec?.decision === "approve") {
                  initial.set(action.action_id, {
                    action_id: action.action_id,
                    status: "pending",
                  });
                } else {
                  initial.set(action.action_id, {
                    action_id: action.action_id,
                    status: "skipped",
                  });
                }
              }
              setExecutionResults(initial);
              advance("execution");
            }}
          />
        )}

        {tab === "queue" && scenario && stage === "execution" && (
          <Execution
            scenario={scenario}
            decisions={decisions}
            results={executionResults}
            setResults={setExecutionResults}
            onComplete={() => advance("resolution")}
          />
        )}

        {tab === "queue" && scenario && stage === "resolution" && (
          <Resolution
            scenario={scenario}
            decisions={decisions}
            results={executionResults}
            onClose={closeIncident}
          />
        )}
      </main>

      <footer className="demo-footnote" role="contentinfo">
        Illustrative demo — the incidents, users, hostnames, integrations, and
        outputs shown are fabricated for the workshop and do not reflect real
        Cloudflare incidents, customers, or production systems.
      </footer>
    </div>
  );
}
